import { Button, message, Modal, Row, Space } from 'antd';
import { useEffect, useRef } from 'react';

import { usePatchChargeMutation } from '../../../../../services/chargeApi';
import { ChargeType, FareType } from '../../../../../typesFinancial';

type FareAllToBillingModalProps = {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  selectedCharge: ChargeType;
};

// A cobrança devolve tarifa principal e complementar no mesmo nível, e cada
// principal ainda repete as complementares aninhadas, então o mesmo id chega duas
// vezes. Achatar e tirar a repetição é o que permite comparar tarifa a tarifa com
// o estado de antes do clique, e o desarquivamento em massa alcança a complementar
// também, porque ela carrega o id da cobrança.
function flattenFares(fares: FareType[]): FareType[] {
  const flattened = fares.flatMap((fare) => [
    fare,
    ...(fare.complementaries || []),
  ]);

  return flattened.filter(
    (fare, index) =>
      flattened.findIndex((other) => other.id === fare.id) === index
  );
}

// O backend desarquiva toda tarifa em ARCHIVED, menos a que deixou movimentação
// órfã no RM: essa continua arquivada, porque nada tira esse débito e devolvê-la a
// "a faturar" liberaria o reenvio do lote que já está no ERP. É o único "ficou de
// fora" do desarquivamento, e o faturista precisa vê-lo, porque a ação dele é no
// RM e não na tela.
//
// Órfã é ter protocolo aceito e não ter o protocolo do lote consolidado: a tarifa
// faturada com sucesso também guarda os protocolos, e essa desarquiva normalmente.
// É por isso que o predicado olha as duas colunas, e não só a primeira.
//
// Espera a lista já achatada, porque os dois chamadores comparam essa retenção com
// outras leituras da mesma lista.
function faresHeldByRm(fares: FareType[]): FareType[] {
  return fares.filter(
    (fare) =>
      fare.status === 'ARCHIVED' &&
      (fare.rm_accepted_record_keys || []).length > 0 &&
      !fare.rm_record_key
  );
}

// A confirmação conta as tarifas retidas em vez de avisar que "tarifas com
// movimentação gravada no RM continuam arquivadas": essa frase descreve também a
// tarifa faturada, estornada e arquivada, que guarda os protocolos e desarquiva
// normalmente, então prometia uma retenção que não acontece. O que retém é o envio
// que não fechou o lote, e é isso que o texto passa a dizer.
//
// A instrução vem junto, porque é no modal que o faturista decide. E ela não pode
// prometer que o estorno no RM destrava a tarifa: nada limpa os protocolos, então a
// guarda do reenvio continua valendo depois dele e ARCHIVED é terminal. O estorno
// serve para o ERP parar de cobrar o que ficou pendurado; cobrar aqueles itens é
// trabalho de uma fatura nova. O "Estornar" da tela também não serve, porque só
// muda o status da tarifa aqui e não fala com o ERP.
function buildConfirmationText(fares: FareType[]): string {
  const opening = 'As tarifas arquivadas desta fatura voltam para "a faturar".';
  const held = faresHeldByRm(flattenFares(fares));

  if (!held.length) {
    return `${opening} Deseja continuar?`;
  }

  const warning =
    held.length === 1
      ? '1 delas continua arquivada e não volta: um envio anterior parou no meio e deixou movimentação gravada no TOTVS RM, então reenviá-la cobraria o cliente duas vezes. Os itens dela só podem ser cobrados por uma fatura nova, depois que essa movimentação for estornada dentro do RM.'
      : `${held.length} delas continuam arquivadas e não voltam: envios anteriores pararam no meio e deixaram movimentações gravadas no TOTVS RM, então reenviá-las cobraria o cliente duas vezes. Os itens delas só podem ser cobrados por uma fatura nova, depois que essas movimentações forem estornadas dentro do RM.`;

  return `${opening} ${warning} Deseja continuar?`;
}

// O cabeçalho sai do que mudou entre os dois estados, e não da existência de tarifa
// retida: quando a única arquivada era a órfã, o backend responde 200 sem ter
// mudado nada, e anunciar "Tarifas desarquivadas" seria mentira na primeira frase,
// que é justamente a que o faturista lê.
function buildUnarchiveFeedback(
  faresBefore: FareType[],
  faresAfter: FareType[]
): {
  type: 'success' | 'warning';
  text: string;
} {
  const after = flattenFares(faresAfter);

  const held = faresHeldByRm(after);

  // Só conta como desarquivada a tarifa que estava em ARCHIVED antes e que a
  // resposta trouxe de volta em outro status. Tarifa ausente da resposta não conta,
  // senão uma omissão viraria desarquivamento inventado.
  const unarchived = flattenFares(faresBefore).filter((fare) => {
    const current = after.find((other) => other.id === fare.id);

    return (
      fare.status === 'ARCHIVED' && !!current && current.status !== 'ARCHIVED'
    );
  });

  if (unarchived.length && !held.length) {
    return { type: 'success', text: 'Tarifas desarquivadas com sucesso' };
  }

  const header = unarchived.length
    ? 'Tarifas desarquivadas.'
    : 'Nenhuma tarifa foi desarquivada.';

  if (!held.length) {
    return { type: 'warning', text: header };
  }

  const clause =
    held.length === 1
      ? '1 tarifa continua arquivada em definitivo, com movimentação de um envio que parou no meio gravada no TOTVS RM.'
      : `${held.length} tarifas continuam arquivadas em definitivo, com movimentação de envios que pararam no meio gravada no TOTVS RM.`;

  return { type: 'warning', text: `${header} ${clause}` };
}

export function FareAllToBillingModal(props: FareAllToBillingModalProps) {
  const { showModal, setShowModal, selectedCharge } = props;

  function closeModal() {
    setShowModal(false);
  }

  const [
    patchCharge,
    {
      data: chargeFares,
      isSuccess: isSuccessPatchCharge,
      isLoading: isLoadingPatchCharge,
    },
  ] = usePatchChargeMutation();

  const faresBeforeUnarchive = useRef<FareType[]>([]);

  useEffect(() => {
    if (isSuccessPatchCharge) {
      const feedback = buildUnarchiveFeedback(
        faresBeforeUnarchive.current,
        chargeFares || []
      );
      message[feedback.type](feedback.text);
    }
    closeModal();
  }, [isSuccessPatchCharge]);

  async function handleFareToArchive() {
    if (selectedCharge && selectedCharge.id) {
      // O estado de antes é guardado no clique, e não lido no efeito: a resposta
      // invalida a tag da cobrança e dispara rebusca, então o `selectedCharge` do
      // render em que o efeito roda já pode ser o de depois, e aí os dois lados da
      // comparação seriam o mesmo estado.
      faresBeforeUnarchive.current = selectedCharge.fare || [];

      patchCharge({
        action: 'billing_statement',
        id: selectedCharge.id,
      });
    }
  }

  return (
    <Modal
      visible={showModal}
      title={<>Confirmar desarquivamento de tarifa</>}
      className="TOSPrimaryModal"
      onCancel={closeModal}
      width={620}
      footer={null}
    >
      <>
        <h2>
          {buildConfirmationText((selectedCharge && selectedCharge.fare) || [])}
        </h2>
        <Row justify="end">
          <Space size={12}>
            <Button
              onClick={() => closeModal()}
              disabled={isLoadingPatchCharge}
            >
              Cancelar
            </Button>
            <Button
              type="primary"
              onClick={() => handleFareToArchive()}
              loading={isLoadingPatchCharge}
            >
              Sim, Desarquivar
            </Button>
          </Space>
        </Row>
      </>
    </Modal>
  );
}
